import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";

/**
 * Site Content — Owner-editable copy for hero, sections, archive, about, contact, footer.
 * Grouped tabs; save button applies all sections; public /settings/public reflects changes.
 */
const GROUPS = [
  { key: "hero", label: "Hero",
    fields: [
      ["headline", "Hero Headline"], ["subheadline", "Hero Subheadline"],
      ["supporting_copy", "Supporting Copy", "textarea"],
      ["primary_cta_text", "Primary CTA Text"], ["primary_cta_link", "Primary CTA Link"],
      ["secondary_cta_text", "Secondary CTA Text"], ["secondary_cta_link", "Secondary CTA Link"],
      ["background_image", "Hero Background Image URL"],
      ["logo_asset", "Hero Logo Asset URL"],
      ["show_logo", "Show Logo", "bool"],
      ["animated_logo_enabled", "Enable Animated Logo (future — kept OFF)", "bool"],
      ["animation_fallback_image", "Animation Fallback Image URL"],
    ]},
  { key: "announcement", label: "Announcement",
    fields: [["active", "Show Announcement", "bool"], ["text", "Announcement Text", "textarea"]]},
  { key: "sections", label: "Section Headings",
    fields: [
      ["featured_heading", "Featured Section Heading"], ["featured_subhead", "Featured Subhead"],
      ["new_arrivals_heading", "New Arrivals Heading"], ["new_arrivals_subhead", "New Arrivals Subhead"],
      ["collections_heading", "Collections Section Heading"],
      ["one_of_one_heading", "One-of-One Heading"], ["one_of_one_subhead", "One-of-One Subhead"],
      ["archive_preview_heading", "Archive Preview Heading"],
      ["story_heading", "Brand Story Heading"],
      ["story_body", "Brand Story Body", "textarea"],
    ]},
  { key: "archive", label: "Sold / Archive Wording",
    fields: [
      ["nav_label", "Navigation Label (e.g. Found a New Home)"],
      ["page_heading", "Archive Page Heading"],
      ["intro", "Archive Intro", "textarea"],
      ["badge_label", "Sold Product Badge (e.g. Found a New Home)"],
    ]},
  { key: "about", label: "About Page",
    fields: [["heading","Page Heading"],["intro","Intro","textarea"],["body","Body","textarea"],["supporting_heading","Supporting Heading"]]},
  { key: "contact", label: "Contact Page",
    fields: [["heading","Page Heading"],["intro","Intro","textarea"],["supporting","Supporting Copy","textarea"]]},
  { key: "footer", label: "Footer",
    fields: [["business_description","Business Description","textarea"],["cta_text","CTA Text"],["supporting_line","Supporting Line"]]},
  { key: "empty_states", label: "Empty States",
    fields: [["shop_empty","Shop Empty Message"],["archive_empty","Archive Empty"],["search_empty","Search Empty"]]},
  { key: "promo_banner", label: "Promotional Banner",
    fields: [
      ["active","Active","bool"], ["message","Message"], ["cta_text","CTA Text"], ["cta_link","CTA Link"],
      ["start_date","Start Date (YYYY-MM-DD)"], ["end_date","End Date (YYYY-MM-DD)"],
    ]},
  { key: "email_signup", label: "Email Signup",
    fields: [
      ["active","Active","bool"],
      ["heading","Heading"], ["description","Description","textarea"],
      ["button_text","Button Text"], ["success_message","Success Message"], ["consent_text","Consent Text","textarea"],
    ]},
  { key: "seo", label: "Page SEO",
    fields: [
      ["home_title","Home SEO Title"], ["home_description","Home Meta Description","textarea"],
      ["social_title","Social Preview Title","","How the homepage title may appear when shared in Messages/social. Blank = uses Home SEO Title. Changes to cached/non-JS previews may require a site republish."],
      ["social_description","Social Preview Description","textarea","How the homepage description may appear when shared in Messages/social. Blank = uses Home Meta Description. Changes to cached/non-JS previews may require a site republish."],
      ["shop_title","Shop SEO Title"], ["shop_description","Shop Meta Description","textarea"],
      ["about_title","About SEO Title"], ["about_description","About Meta Description","textarea"],
      ["contact_title","Contact SEO Title"], ["contact_description","Contact Meta Description","textarea"],
      ["archive_title","Archive SEO Title"], ["archive_description","Archive Meta Description","textarea"],
      ["canonical_base","Canonical Base URL (optional)"],
    ]},
];

export default function SiteContent() {
  const { refresh } = useSettings();
  const [data, setData] = useState(null);
  const [active, setActive] = useState("hero");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-parchment/70">Loading…</div>;

  const upd = (group, key, v) => setData((s) => ({ ...s, [group]: { ...(s[group] || {}), [key]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", data);
      await refresh();
      toast.success("Site content saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const preview = () => window.open("/", "_blank");
  const g = GROUPS.find((x) => x.key === active);
  return (
    <div>
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <h1 className="font-display text-2xl sm:text-3xl text-gold flex-1">Site Content</h1>
        <button data-testid="content-preview" onClick={preview} className="btn-outline text-xs">View Storefront</button>
        <button data-testid="content-save" onClick={save} disabled={saving} className="btn-gold text-xs">{saving ? "Saving…" : "Save Changes"}</button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-brass-soft pb-3">
        {GROUPS.map((x) => (
          <button key={x.key} data-testid={`content-tab-${x.key}`}
            onClick={() => setActive(x.key)}
            className={`font-caption text-[10px] tracking-wider px-3 py-1.5 border ${active === x.key ? "border-gold text-gold" : "border-brass-soft text-parchment/70 hover:text-gold"}`}>
            {x.label}
          </button>
        ))}
      </div>

      <div className="curio-card p-6 space-y-4">
        {g.fields.map(([key, label, type, help]) => {
          const v = data[g.key]?.[key] ?? (type === "bool" ? false : "");
          if (type === "bool") return (
            <label key={key} className="flex items-center gap-2 text-sm text-parchment/85">
              <input type="checkbox" checked={!!v} onChange={(e) => upd(g.key, key, e.target.checked)}
                data-testid={`content-${g.key}-${key}`} /> {label}
            </label>
          );
          if (type === "textarea") return (
            <div key={key}>
              <label className="font-caption text-[10px] text-brass block mb-1">{label}</label>
              <textarea rows={3} className="input-vintage" value={v || ""} data-testid={`content-${g.key}-${key}`}
                onChange={(e) => upd(g.key, key, e.target.value)} />
              {help && <p className="text-parchment/45 text-[10px] mt-1 leading-snug">{help}</p>}
            </div>
          );
          return (
            <div key={key}>
              <label className="font-caption text-[10px] text-brass block mb-1">{label}</label>
              <input className="input-vintage" value={v || ""} data-testid={`content-${g.key}-${key}`}
                onChange={(e) => upd(g.key, key, e.target.value)} />
              {help && <p className="text-parchment/45 text-[10px] mt-1 leading-snug">{help}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
